import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  COMPACT_VIEWPORT_QUERY,
  PREFERS_CONTRAST_MORE_QUERY,
  PREFERS_REDUCED_MOTION_QUERY,
  useMatchMedia,
} from './useMatchMedia';
import {
  hasStoredUiPreferences,
  loadUiPreferences,
  saveUiPreferences,
  type LayoutDensity,
  type StoredUiPreferences,
} from './uiPreferencesStorage';
import { detectBrowserLocale, syncDocumentLocale } from './locale';
import { notifyLocaleChange, setLocale } from './useAscendLocale';
import { resolvePresentation } from '../data/resolvePresentation';
import type {
  AccessibilityPreferences,
  Locale,
  PresentationPreferences,
  ResolvedPresentation,
} from '../data/presentation-types';
import type { DailyRoutineDetails } from '../../../application/dto/DailyRoutineDetails';
import type { RoutineError } from '../../../application/errors/RoutineError';
import type { DailyMissionsClient } from '../../../application/services/DailyMissionsService';
import type { EmotionalState } from '../../../domain/emotion/enums';
import type { DailyProgress } from '../../../domain/progression/CalculateDailyProgress';
import type { DayMode } from '../../../domain/routine/enums';
import type { DailyStep } from '../../../domain/routine/models';

export type AscendSessionStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AscendSessionState {
  readonly status: AscendSessionStatus;
  readonly routineDetails: DailyRoutineDetails | null;
  readonly mode: DayMode | null;
  readonly emotionalState: EmotionalState | null;
  readonly missions: readonly DailyStep[];
  readonly progress: DailyProgress | null;
  readonly resolvedPresentation: ResolvedPresentation | null;
  readonly firstName: string;
  readonly locale: Locale;
  readonly headerExpanded: boolean;
  readonly thoughtExpanded: boolean;
  readonly layoutDensity: LayoutDensity;
  readonly preferences: PresentationPreferences;
  readonly accessibility: AccessibilityPreferences;
  readonly error: RoutineError | null;
  readonly modeChangePending: boolean;
  readonly emotionalStateChangePending: boolean;
}

export interface AscendSessionActions {
  readonly changeMode: (mode: DayMode) => Promise<void>;
  readonly changeEmotionalState: (state: EmotionalState | null) => Promise<void>;
  readonly changeLocale: (locale: Locale) => void;
  readonly setHeaderExpanded: (value: boolean) => void;
  readonly setThoughtExpanded: (value: boolean) => void;
  readonly setLayoutDensity: (value: LayoutDensity) => void;
  readonly updatePresentationPreferences: (patch: Partial<PresentationPreferences>) => void;
  readonly updateAccessibilityPreferences: (patch: Partial<AccessibilityPreferences>) => void;
  readonly reload: () => Promise<void>;
  /**
   * Applique directement une réponse déjà obtenue (démarrer/terminer/reporter/réordonner...),
   * sans re-solliciter `getToday()` — l'appelant possède déjà l'état à jour renvoyé par l'action
   * qu'il vient d'exécuter ; passer par `reload()` ici referait un aller-retour inutile et, avec
   * un client dont `getToday()` ne reflète pas instantanément l'écriture, régresserait l'état
   * affiché.
   */
  readonly applyRoutineDetails: (details: DailyRoutineDetails) => void;
}

export type UseAscendSessionResult = AscendSessionState & AscendSessionActions;

export interface UseAscendSessionOptions {
  /** Désactive le chargement automatique au montage — utile pour les tests. Défaut : `true`. */
  readonly autoLoad?: boolean;
  /** Injection de dépendance pour les tests ; défaut : `navigator.languages`. */
  readonly browserLanguages?: readonly string[];
}

/**
 * Session de présentation ASCEND V7 : relie les données applicatives réelles (via le
 * `DailyMissionsClient` déjà injecté par l'appelant — jamais recréé ici) aux préférences
 * d'interface locales, et produit la `ResolvedPresentation` correspondante.
 *
 * N'accède jamais à Dexie, n'instancie aucun repository, ne recrée aucun use case. Le mode et
 * l'état émotionnel restent entièrement portés par le client applicatif ; seules les
 * préférences purement visuelles (locale, header, pensée, accessibilité, densité cosmétique)
 * sont possédées et persistées ici.
 */
export function useAscendSession(
  client: DailyMissionsClient,
  options: UseAscendSessionOptions = {},
): UseAscendSessionResult {
  const { autoLoad = true, browserLanguages } = options;

  const isCompactViewport = useMatchMedia(COMPACT_VIEWPORT_QUERY);
  const systemReducedMotion = useMatchMedia(PREFERS_REDUCED_MOTION_QUERY);
  const systemContrastMore = useMatchMedia(PREFERS_CONTRAST_MORE_QUERY);

  const [status, setStatus] = useState<AscendSessionStatus>(autoLoad ? 'loading' : 'idle');
  const [routineDetails, setRoutineDetails] = useState<DailyRoutineDetails | null>(null);
  const [error, setError] = useState<RoutineError | null>(null);
  const [modeChangePending, setModeChangePending] = useState(false);
  const [emotionalStateChangePending, setEmotionalStateChangePending] = useState(false);

  const [uiPreferences, setUiPreferences] = useState<StoredUiPreferences>(() => {
    const stored = loadUiPreferences();
    if (hasStoredUiPreferences()) return stored;
    const detectedLocale = detectBrowserLocale(browserLanguages ?? readNavigatorLanguages());
    return { ...stored, locale: detectedLocale, presentationPreferences: { ...stored.presentationPreferences, locale: detectedLocale } };
  });

  const requestGeneration = useRef(0);
  // Initialisé à `false` et mis à `true` par l'effet ci-dessous (jamais à la déclaration du ref) :
  // en React 18 `StrictMode`, le développement rejoue chaque effet (montage → nettoyage →
  // montage) sur la même instance sans réinitialiser les refs. Si ce ref ne portait la valeur
  // `true` qu'à sa déclaration, le premier passage de nettoyage le figerait à `false` de façon
  // permanente et toute réponse de `load()` arrivant ensuite serait ignorée — l'écran restant
  // bloqué sur « Chargement de la journée… ». Le remettre à `true` dans le corps de l'effet
  // garantit qu'il reflète l'état de montage réel après le rejeu.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    saveUiPreferences(uiPreferences);
  }, [uiPreferences]);

  useEffect(() => {
    notifyLocaleChange(uiPreferences.locale);
    syncDocumentLocale(uiPreferences.locale);
  }, [uiPreferences.locale]);

  const load = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setStatus('loading');
    setError(null);
    const result = await client.getToday();
    if (!isMountedRef.current || generation !== requestGeneration.current) return;
    if (result.ok) {
      setRoutineDetails(result.value);
      setStatus('ready');
    } else {
      setError(result.error);
      setStatus('error');
    }
  }, [client]);

  useEffect(() => {
    if (!autoLoad) return;
    // Chargement initial uniquement : `reload()` couvre les rechargements explicites ultérieurs.
    // Différé d'une microtâche pour ne jamais appeler de setState de façon synchrone dans le
    // corps de l'effet (voir règle `react-hooks/set-state-in-effect`) — `load` reste la seule
    // source de vérité pour la logique de chargement, réutilisée aussi par `reload()`.
    void Promise.resolve().then(() => load());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMode = useCallback(
    async (mode: DayMode) => {
      if (!routineDetails || modeChangePending) return;
      setModeChangePending(true);
      setError(null);
      const result = await client.changeMode(routineDetails.routine.logicalDate, mode);
      if (!isMountedRef.current) return;
      if (result.ok) {
        setRoutineDetails(result.value);
      } else {
        setError(result.error);
      }
      setModeChangePending(false);
    },
    [client, routineDetails, modeChangePending],
  );

  const changeEmotionalState = useCallback(
    async (state: EmotionalState | null) => {
      if (!routineDetails || emotionalStateChangePending) return;
      setEmotionalStateChangePending(true);
      setError(null);
      const result = await client.setEmotionalState(routineDetails.routine.logicalDate, state);
      if (!isMountedRef.current) return;
      if (result.ok) {
        setRoutineDetails(result.value);
      } else {
        setError(result.error);
      }
      setEmotionalStateChangePending(false);
    },
    [client, routineDetails, emotionalStateChangePending],
  );

  const applyRoutineDetails = useCallback((details: DailyRoutineDetails) => {
    setError(null);
    setRoutineDetails(details);
    setStatus('ready');
  }, []);

  const changeLocale = useCallback((locale: Locale) => {
    const normalized = setLocale(locale);
    setUiPreferences((previous) => ({
      ...previous,
      locale: normalized,
      presentationPreferences: { ...previous.presentationPreferences, locale: normalized },
    }));
  }, []);

  const setHeaderExpanded = useCallback((value: boolean) => {
    setUiPreferences((previous) => ({ ...previous, headerExpanded: value }));
  }, []);

  const setThoughtExpanded = useCallback((value: boolean) => {
    setUiPreferences((previous) => ({ ...previous, thoughtExpanded: value }));
  }, []);

  const setLayoutDensity = useCallback((value: LayoutDensity) => {
    setUiPreferences((previous) => ({ ...previous, layoutDensity: value }));
  }, []);

  const updatePresentationPreferences = useCallback((patch: Partial<PresentationPreferences>) => {
    setUiPreferences((previous) => ({
      ...previous,
      presentationPreferences: { ...previous.presentationPreferences, ...patch },
    }));
  }, []);

  const updateAccessibilityPreferences = useCallback((patch: Partial<AccessibilityPreferences>) => {
    setUiPreferences((previous) => ({
      ...previous,
      accessibilityPreferences: { ...previous.accessibilityPreferences, ...patch },
    }));
  }, []);

  const mode = routineDetails?.routine.dayMode ?? null;
  const emotionalState = routineDetails?.emotionalState ?? null;

  // Une préférence explicite d'accessibilité l'emporte toujours ; sans choix explicite, le
  // système (`prefers-reduced-motion` / `prefers-contrast`) fournit la valeur initiale.
  const accessibility: AccessibilityPreferences = {
    ags: uiPreferences.accessibilityPreferences.ags || systemContrastMore,
    reducedMotion: uiPreferences.accessibilityPreferences.reducedMotion || systemReducedMotion,
  };

  const resolvedPresentation = useMemo<ResolvedPresentation | null>(() => {
    if (!mode) return null;
    return resolvePresentation({
      mode,
      state: emotionalState,
      prefs: uiPreferences.presentationPreferences,
      a11y: accessibility,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, emotionalState, uiPreferences.presentationPreferences, accessibility.ags, accessibility.reducedMotion]);

  return {
    status,
    routineDetails,
    mode,
    emotionalState,
    missions: routineDetails?.steps ?? [],
    progress: routineDetails?.progress ?? null,
    resolvedPresentation,
    firstName: uiPreferences.firstName ?? '',
    locale: uiPreferences.locale,
    headerExpanded: uiPreferences.headerExpanded ?? !isCompactViewport,
    thoughtExpanded: uiPreferences.thoughtExpanded,
    layoutDensity: uiPreferences.layoutDensity,
    preferences: uiPreferences.presentationPreferences,
    accessibility,
    error,
    modeChangePending,
    emotionalStateChangePending,
    changeMode,
    changeEmotionalState,
    changeLocale,
    setHeaderExpanded,
    setThoughtExpanded,
    setLayoutDensity,
    updatePresentationPreferences,
    updateAccessibilityPreferences,
    reload: load,
    applyRoutineDetails,
  };
}

function readNavigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages && navigator.languages.length > 0) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}
